import * as React from 'react';
import styled from '@emotion/styled';
import { Flex } from 'grid-styled';
import {
  createRefetchContainer,
  graphql,
  RelayRefetchProp,
  // @ts-ignore: no context import
  ReactRelayContext,
} from 'react-relay';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import { Dialog } from '@material-ui/core';
import { MuiButton } from '@onehope/design-system-ohw';
import CloseIcon from 'material-ui/svg-icons/navigation/close';

/* Components */
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import ShopWithCeoProfile from './ShopWithCeoProfile';

/* Helpers */
import { ShopWithCeoDialog_user } from './__generated__/ShopWithCeoDialog_user.graphql';
import { ceEnrollmentLaunchDate } from '../../../../server/utils/helpers';
// import { ROLES } from '../../../../server/config';
/* Styles */
import './ShopWithCeoDialog.scss';
import { dialogStyle } from '../../../styles/global.style';
import { mobile } from '../../../utils/deviceWidths';
import ShopWithCeoDeleteMutation from '../../../mutationsModern/User/ShopWithCeoDeleteMutation';
import CartUpdateEventIdMutation from '../../../mutationsModern/Cart/CartUpdateEventIdMutation';
import CartUpdatePromoCodesMutation from '../../../mutationsModern/Cart/CartUpdatePromoCodesMutation';

const toolbarHeight = 60;
const actionsHeight = 85;

const Close = styled(CloseIcon)`
  position: absolute;
  top: 28px;
  right: 28px;
  cursor: pointer;
`;

interface ShopWithCeoDialogProps {
  ceoId: string | null;
  containerWidth: number;
  handleRouteChange: (path: string) => void;
  handleCartHotSwap: () => void;
  handleExitEvent: (callback?: () => void) => void;
  shoppingWithCeoUser?: any;
  handleAuthentication: () => void;
  isShopWithCeoDialogOpen: boolean;
  isDisabled?: boolean;
  handleCloseShopWithCeoDialog: () => void;
  handleSelectedEvent?: () => void;
  handleShopWithCeo: (input: string | null, cb: () => void) => void;
  user: ShopWithCeoDialog_user;
  relay: RelayRefetchProp;
}

interface ShopWithCeoDialogState {
  hasFetchedCeoData: boolean;
  selectedEvent: string;
}

class ShopWithCeoDialog extends React.Component<
  ShopWithCeoDialogProps,
  ShopWithCeoDialogState
> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state = {
    selectedEvent: '',
    hasFetchedCeoData: false,
  };

  componentDidMount() {
    this._refetch();
  }

  _refetch = () => {
    const { relay, ceoId } = this.props;
    relay.refetch(
      { ceoId }, // Our refetchQuery needs to know the `ceoId`
      undefined,
      () => {
        this.setState({ hasFetchedCeoData: true });
      },
      { force: true }, // Assuming we've configured a network layer cache, we want to ensure we fetch the latest data.
    );
  };

  handleClose = () => {
    const { handleCloseShopWithCeoDialog } = this.props;
    handleCloseShopWithCeoDialog();
  };

  handleJoinTeam = () => {
    const {
      user: { ceo },
    } = this.props;
    if (ceo) {
      const ceEnrollmentStart = DateTime.fromISO(ceEnrollmentLaunchDate);
      const now = DateTime.local();

      if (now > ceEnrollmentStart) {
        // Hack for launch... set full url so we can pass query params. opens in new window...
        this.props.handleRouteChange(
          `${process.env.SERVER_DNS}/join?dealer=${ceo.trinityDealerId}`,
        );
      } else {
        this.props.handleRouteChange(
          `https://backoffice.onehopewine.com/MemberToolsDotNet/EnrollmentNew/StartPublicEnrollment.aspx?referringDealerId=${
            ceo.trinityDealerId
          }`,
        );
      }
    }
  };

  handleAddHost = (eventId: string) => {
    const {
      relay: { environment },
    } = this.props;
    CartUpdateEventIdMutation.commit({
      environment,
      eventId,
      onCompleted: (response: {}, error: {}) => {
        console.log('response update event id', response);
        // handleCartHotSwap();
        CartUpdatePromoCodesMutation.commit({
          environment,
          promoCodes: 'CASE4CAUSE',
          key: 'event',
          noGiftCards: false,
          onCompleted: (response: {}, error: {}) => {
            console.log('response promo', response);
          },
          onError: (transaction: {}) => {
            console.log('TRANS in error', transaction);
          },
        });
      },
      onError: (transaction: {}) => {
        console.log('error for update event id', transaction);
      },
    });
  };

  handleShopWithCeo = () => {
    const { handleShopWithCeo, ceoId } = this.props;
    const { router } = this.context;
    const { selectedEvent } = this.state;

    const regExp = new RegExp(/([^\/]*)\/*$/);
    const event = selectedEvent ? selectedEvent.match(regExp) : null;
    const location = window.location.pathname.match(regExp);
    const eventLocationId = location ? location[0] : null;
    const eventId = event ? event[1] : null;
    const callback = selectedEvent ? router.push(selectedEvent) : () => {};
    handleShopWithCeo(ceoId, callback);
    if (eventId && eventLocationId && eventId === eventLocationId) {
      this.handleAddHost(eventId);
    }
  };

  handleSelectedEvent = (selectedEvent: string) => {
    this.setState({ selectedEvent });
  };

  exitEvent = () => {
    const { handleExitEvent } = this.props;
    handleExitEvent(this.handleClose);
  };

  // This fn currently is not used.
  cancelShoppingWithCeo = () => {
    const { relay, handleCartHotSwap } = this.props;
    const { environment } = relay;
    ShopWithCeoDeleteMutation.commit({
      environment,
      onCompleted: () => {
        handleCartHotSwap();
        console.log('customer shopping removed');
        this.exitEvent();
      },
      onError: (trans: any) => {
        console.log('TRANS error: ', trans);
      },
    });
  };

  render() {
    const {
      isDisabled,
      user,
      user: { ceo },
    } = this.props;
    if (!user) return <div />;
    const disableJoinMyTeam = ceo && ceo.disableJoinMyTeam;

    const {
      isShopWithCeoDialogOpen,
      containerWidth,
      handleRouteChange,
      handleAuthentication,
      handleCartHotSwap,
      ceoId,
    } = this.props;
    const { hasFetchedCeoData } = this.state;
    const isDialogOpen = isShopWithCeoDialogOpen && hasFetchedCeoData;
    const isMobile = containerWidth <= mobile;
    const contentStyle = isMobile
      ? {
          maxWidth: 600,
          padding: 0,
          transform: 'translate(0, 0)',
          width: '100%',
        }
      : {
          width: '100%',
          maxWidth: 600,
        };

    const dialogBodyStyle = isMobile
      ? {
          minHeight: `calc(100vh - ${toolbarHeight + actionsHeight}px)`,
          padding: 16,
        }
      : {
          padding: 30,
        };

    const bodyClassName = `dialog-body${isMobile ? ' mobile' : ''}`;
    return (
      <Flex wrap className="ShopWithCeoDialog">
        <DialogWrapper isDialogOpen={isDialogOpen}>
          <Dialog
            modal={false}
            fullScreen={isMobile}
            style={dialogStyle}
            open={isDialogOpen}
            autoScrollBodyContent
            className="dialog-root"
            repositionOnUpdate={false}
            contentStyle={contentStyle}
            bodyStyle={dialogBodyStyle}
            bodyClassName={bodyClassName}
            autoDetectWindowHeight={false}
            onRequestClose={this.handleClose}
          >
            {user && ceoId && ceo && (
              <div>
                <Close onClick={this.handleClose} />
                <ReactRelayContext.Provider
                  value={{
                    environment: this.props.relay.environment,
                    variables: {},
                  }}
                >
                  <ShopWithCeoProfile
                    // relay={this.props.relay}
                    user={user}
                    ceoId={ceoId}
                    profile={ceo.profile}
                    lastName={ceo.lastName}
                    firstName={ceo.firstName}
                    containerWidth={containerWidth}
                    handleCartHotSwap={handleCartHotSwap}
                    handleRouteChange={handleRouteChange}
                    handleAuthentication={handleAuthentication}
                    handleSelectedEvent={this.handleSelectedEvent}
                    cancelShoppingWithCeo={this.cancelShoppingWithCeo}
                  />
                </ReactRelayContext.Provider>
                <div className="ShopWithMeButton">
                  {!disableJoinMyTeam && (
                    <>
                      <MuiButton
                        type="SECONDARY"
                        fullWidth
                        disabled={isDisabled}
                        onClick={this.handleJoinTeam}
                        data-cypress="JoinMyTeam"
                      >
                        Join My Team
                      </MuiButton>
                      <div className="Spacer" />
                    </>
                  )}
                  <MuiButton
                    type="PRIMARY"
                    fullWidth
                    disabled={isDisabled}
                    onClick={this.handleShopWithCeo}
                    data-cypress="ShopWithMe"
                  >
                    Shop with Me
                  </MuiButton>
                </div>
              </div>
            )}
          </Dialog>
        </DialogWrapper>
      </Flex>
    );
  }
}

export default createRefetchContainer(
  ShopWithCeoDialog,
  {
    user: graphql`
      fragment ShopWithCeoDialog_user on User
        @argumentDefinitions(ceoId: { type: "String", defaultValue: null }) {
        role
        id
        cart {
          id
          eventId
        }
        ...ShopWithCeoProfile_user
        ceo(ceoId: $ceoId) {
          id
          userId
          firstName
          disableJoinMyTeam
          lastName
          trinityDealerId
          profile {
            image
            summary
            city
            state
            zip
          }
        }
      }
    `,
  },
  graphql`
    query ShopWithCeoDialogRefetchQuery($ceoId: String) {
      viewer {
        id
        id
        user {
          id
          id
          ...ShopWithCeoDialog_user @arguments(ceoId: $ceoId)
        }
      }
    }
  `,
);
